import React, { useContext, useState, useMemo } from 'react';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Avatar,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  tooltipClasses,
  TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import './CollapsibleList.css';
import { LogAnalyticsEvent } from '../../utils/firebase/firebase';
import { ANALYTICS_KEYS } from '../../constants/AnalyticKeys';
import { UIContext } from '../../contexts/UIProvider';
import { usePreRegisteredUsers } from '../../contexts/userDataContext';

const BootstrapTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
    fontWeight: 'bold',
    padding: '10px',
  },
}));

const CollapsibleList = ({
  mainIcon,
  primaryText,
  data = [],
  onItemClick,
  isDrawerExpanded,
  isHovering,
  getItemId = (item) => item.id,
  getItemName = (item) => item.name,
  getItemAvatar = (item) => item.photoURL,
  getItemTooltip = (item) => null,
}) => {
  const { selectedPatientId } = useContext(UIContext);

  const [open, setOpen] = useState(selectedPatientId !== -1 || false);
  const [openedByClick, setOpenedByClick] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: preRegisteredUsers = [],
    // eslint-disable-next-line no-unused-vars
    isLoading,
    // eslint-disable-next-line no-unused-vars
    error: errorStudentData,
  } = usePreRegisteredUsers();

  // Memoize the filtered users to prevent unnecessary recalculations
  const filteredUsers = useMemo(() => {
    const combinedData = [...data, ...preRegisteredUsers];
    const getDisplayName = (user) =>
      user.patientInfo?.fullname || user.name || '';

    const sortedData = (Array.isArray(combinedData) ? [...combinedData] : [])
      .sort((a, b) =>
        getDisplayName(a)
          .toLowerCase()
          .localeCompare(getDisplayName(b).toLowerCase())
      );

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return sortedData.filter((user) =>
        getDisplayName(user).toLowerCase().includes(query)
      );
    }

    return sortedData;
  }, [data, preRegisteredUsers, searchQuery]);

  const handleClick = () => {
    const newOpenState = !open;
    setOpen(newOpenState);
    setOpenedByClick(newOpenState); // <-- Track if opened by click
    LogAnalyticsEvent(ANALYTICS_KEYS.LEFTMENU.SIDEBARMENUCLICK, {
      page_name: primaryText,
      is_open: newOpenState,
    });
  };

  const handleMouseEnter = () => {
    setOpen(true);
  };

  const handleMouseLeave = () => {
    if (!openedByClick) {
      setOpen(false);
    }
  };

  const truncateName = (name, limit = 10) => {
    if (!name) return '';
    const capitalized = name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    return capitalized.length > limit
      ? `${capitalized.slice(0, limit)}...`
      : capitalized;
  };

  const getRandomPastelColor = (name) => {
    const hash = [...name].reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 80%)`;
  };

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{ width: '100%' }}
      >
        <ListItemIcon sx={{ color: 'var(--clr-theme-purple)' }}>
          {mainIcon}
        </ListItemIcon>
        {(isDrawerExpanded || isHovering) && (
          <ListItemText
            primary={primaryText}
            sx={{ color: 'var(--clr-theme-gray-dark)' }}
          />
        )}
        {open ? (
          <ExpandLess sx={{ color: 'var(--clr-theme-gray-dark)' }} />
        ) : (
          <ExpandMore sx={{ color: 'var(--clr-theme-gray-dark)' }} />
        )}
      </ListItemButton>

      <Collapse
        in={open}
        timeout='auto'
        unmountOnExit
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {(isDrawerExpanded || isHovering) && (
          <TextField
            id='user-search-field'
            label='Search'
            variant='outlined'
            fullWidth
            size='small'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              mt: 1,
              mb: 1,
              background: 'var(--clr-theme-bg)',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              },
              '& .MuiInputLabel-root': {
                color: 'var(--clr-theme-gray-dark)',
              },
            }}
          />
        )}

        <List
          component='div'
          disablePadding
          className='collapsedList'
          sx={{
            overflowX: isDrawerExpanded || isHovering ? 'auto' : 'hidden',
            width: isDrawerExpanded || isHovering ? 'auto' : 50,
          }}
        >
          {filteredUsers.map((item) => (
            <ListItemButton
              key={getItemId(item)}
              sx={{
                pl: isDrawerExpanded || isHovering ? 2 : 1.5,
                width: isDrawerExpanded || isHovering ? '100%' : 45,
              }}
              className={
                selectedPatientId === getItemId(item) ? 'selectedPatient' : ''
              }
              onClick={() => onItemClick(item)}
            >
              <ListItemIcon
                sx={{
                  justifyContent:
                    isDrawerExpanded || isHovering ? 'auto' : 'left',
                  width: isDrawerExpanded || isHovering ? 'auto' : 40,
                }}
              >
                <BootstrapTooltip
                  title={getItemTooltip(item) || getItemName(item)}
                  arrow
                >
                  <Avatar
                    src={getItemAvatar(item) || undefined}
                    sx={{
                      width: isDrawerExpanded || isHovering ? 40 : 24,
                      height: isDrawerExpanded || isHovering ? 40 : 24,
                      bgcolor: !getItemAvatar(item)
                        ? getRandomPastelColor(getItemName(item) || '')
                        : 'transparent',
                      color: !getItemAvatar(item) ? '#000000' : 'inherit',
                      justifyContent:
                        isDrawerExpanded || isHovering ? 'auto' : 'center',
                      fontWeight: 'bold',
                      fontSize:
                        isDrawerExpanded || isHovering ? '0.9rem' : '0.7rem',
                    }}
                  >
                    {!getItemAvatar(item) &&
                      (getItemName(item)?.[0]?.toUpperCase() || '')}
                  </Avatar>
                </BootstrapTooltip>
              </ListItemIcon>
              {(isDrawerExpanded || isHovering) && (
                <BootstrapTooltip
                  title={getItemTooltip(item) || getItemName(item)}
                  arrow
                >
                  <ListItemText
                    primary={truncateName(getItemName(item))}
                    sx={{ cursor: 'pointer' }}
                  />
                </BootstrapTooltip>
              )}
            </ListItemButton>
          ))}
        </List>
      </Collapse>
    </>
  );
};

export default CollapsibleList;
